import React, { useState, useMemo } from 'react';
import { RawTrader, TraderDerivation } from './types';
import { historyRows, chipStyle } from './constants';

interface HistoryTabProps {
  trader: RawTrader;
  derivation: TraderDerivation;
}

export const HistoryTab: React.FC<HistoryTabProps> = ({ trader, derivation: d }) => {
  const [hq, setHq] = useState<string>('');
  const [hf, setHf] = useState<string>('all');

  const allHistory = useMemo(() => historyRows(trader, d), [trader, d]);

  const filteredHistory = useMemo(() => {
    let hist = allHistory.slice();
    if (hq.trim()) hist = hist.filter((h) => h.sym.toLowerCase().includes(hq.trim().toLowerCase()));
    if (hf === 'buy') hist = hist.filter((h) => h.dir === 'BUY');
    if (hf === 'sell') hist = hist.filter((h) => h.dir === 'SELL');
    if (hf === 'win') hist = hist.filter((h) => h.win);
    if (hf === 'loss') hist = hist.filter((h) => !h.win);
    return hist;
  }, [allHistory, hq, hf]);

  return (
    <div className="card-box overflow-hidden">
      <div className="p-4 md:p-5 border-b border-[#EEF1F5] flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-bold text-gray-900">Lịch sử giao dịch</div>
          <div className="text-xs text-[#8B95A3] mt-0.5">
            {d.trades} lệnh đã đóng · hiển thị 16 lệnh gần nhất
          </div>
        </div>
        <div className="flex items-center gap-2 flex-initial w-44 border border-[#E7EBF0] !rounded-lg px-3 py-2 bg-white">
          <input
            type="text"
            value={hq}
            onChange={(e) => setHq(e.target.value)}
            placeholder="Tìm symbol"
            className="border-0 outline-none bg-transparent text-xs text-[#16202E] w-full"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[
            ['all', 'Tất cả'],
            ['buy', 'Mua'],
            ['sell', 'Bán'],
            ['win', 'Thắng'],
            ['loss', 'Thua'],
          ].map(([k, label]) => {
            const active = hf === k;
            const cStyle = chipStyle(active);
            return (
              <button
                key={k}
                onClick={() => setHf(k)}
                style={cStyle}
                className="cursor-pointer py-2 px-3 text-xs font-semibold !rounded-lg border whitespace-nowrap outline-none"
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {filteredHistory.length === 0 && (
        <div className="p-12 text-center">
          <div className="text-sm font-bold text-gray-900 mb-1.5">Không có lệnh nào khớp bộ lọc</div>
          <div className="text-xs text-[#6B7684]">Thử đổi symbol hoặc bỏ lọc kết quả.</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-[880px]">
          <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.9fr_0.9fr_0.7fr_1fr_0.9fr_1fr] gap-3 px-5 py-3 border-b border-[#EEF1F5] text-xs font-semibold text-[#8B95A3]">
            <div>Thời gian</div>
            <div>Symbol</div>
            <div>Chiều</div>
            <div className="text-right">Vào</div>
            <div className="text-right">Ra</div>
            <div className="text-right">Lot</div>
            <div className="text-right">Lợi nhuận</div>
            <div className="text-right">% vốn</div>
            <div className="text-right">Thời lượng</div>
          </div>
          {filteredHistory.map((hItem, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1.2fr_1fr_0.8fr_0.9fr_0.9fr_0.7fr_1fr_0.9fr_1fr] gap-3 px-5 py-3.5 border-b border-[#F3F5F8] items-center font-mono text-xs hover:bg-[#FAFBFD]"
            >
              <div className="text-[#8B95A3]">{hItem.date}</div>
              <div className="font-semibold text-gray-900">{hItem.sym}</div>
              <div>
                <span
                  style={{ background: hItem.dirBg, color: hItem.dirFg }}
                  className="font-sans text-[11px] font-semibold px-2 py-0.5 !rounded"
                >
                  {hItem.dir}
                </span>
              </div>
              <div className="text-right text-[#5C6779]">{hItem.entry}</div>
              <div className="text-right text-[#5C6779]">{hItem.exit}</div>
              <div className="text-right text-[#5C6779]">{hItem.lot}</div>
              <div style={{ color: hItem.c }} className="text-right font-semibold">
                {hItem.pnl}
              </div>
              <div style={{ color: hItem.c }} className="text-right">
                {hItem.pct}
              </div>
              <div className="text-right text-[#A6AFBC]">{hItem.dur}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 text-xs text-[#8B95A3] flex justify-between flex-wrap gap-2 border-t border-gray-100 bg-gray-50">
        <span>Hiển thị {filteredHistory.length} lệnh gần nhất · giá vào/ra đã làm tròn</span>
        <span>Không hiển thị số tài khoản MT5 và thông tin broker</span>
      </div>
    </div>
  );
};
